import React, { useEffect, useRef, useState, useMemo } from 'react';
import { X, Smartphone, Phone, Star, Navigation, Loader2, MapPin, Info } from 'lucide-react';
import { Input } from '../ui/Input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/Select';
import commandCenterService from '../../services/commandCenterService';

// Module-level cache so ambulance types are fetched once and never re-load or flicker on re-renders
let cachedVehicleTypeOptions = null;
let fetchingVehicleTypesPromise = null;

// Reasons a dispatcher can give for calling a driver instead of using the app
// they're known to have — required so bypassing the app leaves an audit trail.
const CALL_REASONS = [
  'Driver not responding on app',
  'Urgent — cannot wait',
  'Driver requested call',
  'App issue',
];

const FieldLabel = ({ children, className = '' }) => (
  <label className={`text-xs font-semibold text-gray-600 mb-1.5 block ${className}`}>{children}</label>
);

// Shared "confirm price + notify" popup for both assigning a driver already in
// the nearby list (mode="driver", always has the app for now) and Add & Assign
// — a driver not in the mock pool (mode="manual", no app by definition). Ends
// either by "Assign via App" (mode="driver" only) or "Assign by Call" (always
// available; requires a reason when bypassing an available app), which
// finalizes the assignment in the parent.
const CommandCenterAssignPricingPopup = ({
  mode,
  driver,
  priceFields,
  initialLocation,
  assigning = false,
  onClose,
  onAssign,
  onError,
}) => {
  const hasApp = mode === 'driver';
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const [prices, setPrices] = useState({ ...priceFields });
  const [manualDriver, setManualDriver] = useState({
    name: '',
    phone: '',
    vehicleType: '',
    vehicleTypes: [],
    vehiclePlate: '',
    vendorName: '',
    city: initialLocation?.city || '',
    area: initialLocation?.area || '',
    pincode: initialLocation?.pincode || '',
  });
  const [showCallReason, setShowCallReason] = useState(false);
  const [callReason, setCallReason] = useState('');

  // Initialize from cache if available so there is zero initial loading spinner or flicker
  const [vehicleTypeOptions, setVehicleTypeOptions] = useState(() => cachedVehicleTypeOptions || []);
  const [loadingVehicleTypes, setLoadingVehicleTypes] = useState(() => mode === 'manual' && !cachedVehicleTypeOptions);

  // Add & Assign (mode="manual") only — a last confirmation step before the
  // actual assignment fires, since submitting here also registers a brand
  // new ambulance/driver record, not just a dispatch action.
  const [showAssignConfirm, setShowAssignConfirm] = useState(false);

  // Load ambulance types once and cache them. Does NOT re-fetch on parent re-renders.
  useEffect(() => {
    if (mode !== 'manual') return undefined;

    if (cachedVehicleTypeOptions && cachedVehicleTypeOptions.length > 0) {
      setVehicleTypeOptions(cachedVehicleTypeOptions);
      setLoadingVehicleTypes(false);
      return undefined;
    }

    let cancelled = false;
    setLoadingVehicleTypes(true);

    if (!fetchingVehicleTypesPromise) {
      fetchingVehicleTypesPromise = commandCenterService.getAmbulanceTypes()
        .then((result) => {
          const allTypes = Array.isArray(result?.data) ? result.data : [];
          cachedVehicleTypeOptions = allTypes;
          return allTypes;
        })
        .catch((err) => {
          fetchingVehicleTypesPromise = null;
          throw err;
        });
    }

    fetchingVehicleTypesPromise
      .then((allTypes) => {
        if (cancelled) return;
        setVehicleTypeOptions(allTypes);
      })
      .catch((error) => {
        if (!cancelled) onErrorRef.current?.(error.message || 'Failed to load ambulance types');
      })
      .finally(() => {
        if (!cancelled) setLoadingVehicleTypes(false);
      });

    return () => {
      cancelled = true;
    };
  }, [mode]);

  // Sync initial location from parent if provided, without triggering infinite state changes
  useEffect(() => {
    if (initialLocation && mode === 'manual') {
      setManualDriver((prev) => {
        const nextCity = prev.city || initialLocation.city || '';
        const nextArea = prev.area || initialLocation.area || '';
        const nextPincode = prev.pincode || initialLocation.pincode || '';
        if (prev.city === nextCity && prev.area === nextArea && prev.pincode === nextPincode) {
          return prev;
        }
        return {
          ...prev,
          city: nextCity,
          area: nextArea,
          pincode: nextPincode,
        };
      });
    }
  }, [initialLocation?.city, initialLocation?.area, initialLocation?.pincode, mode]);

  const updatePrice = (key, value) => setPrices((prev) => ({ ...prev, [key]: value }));
  const updateManual = (key, value) => setManualDriver((prev) => ({ ...prev, [key]: value }));

  const activeVehicleTypeOptions = useMemo(() => {
    const list = Array.isArray(vehicleTypeOptions) ? vehicleTypeOptions : [];
    const activeList = list.filter((t) => t.is_active !== false);
    const candidates = activeList.length > 0 ? activeList : list;
    const currentVal = manualDriver.vehicleType || manualDriver.vehicleTypes?.[0];
    if (currentVal && !candidates.some((t) => t.name === currentVal)) {
      const match = list.find((t) => t.name === currentVal);
      if (match) return [...candidates, match];
    }
    return candidates;
  }, [vehicleTypeOptions, manualDriver.vehicleType, manualDriver.vehicleTypes]);

  const selectedVehicleTypeValue = manualDriver.vehicleType || manualDriver.vehicleTypes?.[0] || '';
  const selectedVehicleTypeObj = useMemo(() => {
    return (vehicleTypeOptions || []).find((t) => t.name === selectedVehicleTypeValue);
  }, [vehicleTypeOptions, selectedVehicleTypeValue]);

  const selectedVehicleTypeLabel = selectedVehicleTypeObj
    ? (selectedVehicleTypeObj.abbreviation && !selectedVehicleTypeObj.name.includes(selectedVehicleTypeObj.abbreviation)
        ? `${selectedVehicleTypeObj.name} (${selectedVehicleTypeObj.abbreviation})`
        : selectedVehicleTypeObj.name)
    : selectedVehicleTypeValue;

  const handleVehicleTypeChange = (selectedName) => {
    const cleanName = selectedName?.trim() || '';
    const updatedList = cleanName ? [cleanName] : [];
    setManualDriver((prev) => ({
      ...prev,
      vehicleType: cleanName,
      vehicleTypes: updatedList,
    }));
  };

  const handlePriceFieldChange = (fieldKey, value) => {
    const updated = { ...prices, [fieldKey]: value };
    const fo = Number(updated.fleetOperatorPrice ?? updated.offeredPrice ?? 0);
    const ps = Number(updated.platformServiceFee ?? updated.agreedPrice ?? 0);
    const fos = Number(updated.fleetOperatorServiceFee ?? updated.partnerPrice ?? 0);
    const sum = fo + ps + fos;
    setPrices({
      ...updated,
      totalPrice: sum > 0 ? sum : '',
    });
  };

  const canAssign = (mode === 'driver' || (manualDriver.name.trim() && manualDriver.phone.trim())) && !assigning;

  const buildDriver = () => {
    if (mode === 'driver') return driver;
    const selectedAbbrs = (manualDriver.vehicleTypes || [])
      .map((typeName) => {
        const matched = vehicleTypeOptions.find(
          (t) => t.name === typeName || t.type_id === typeName || t.abbreviation === typeName
        );
        return matched?.abbreviation || typeName;
      })
      .filter(Boolean);
    const abbrLabel = selectedAbbrs.length > 0
      ? selectedAbbrs.join(', ')
      : (manualDriver.vehicleType || 'Not specified');

    const primaryType = manualDriver.vehicleTypes?.[0] || manualDriver.vehicleType || null;

    return {
      id: `manual-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      name: manualDriver.name.trim(),
      phone: manualDriver.phone.trim(),
      vehiclePlate: manualDriver.vehiclePlate.trim() || '—',
      vehicleType: primaryType,
      vehicleTypeLabel: abbrLabel,
      vehicleTypes: manualDriver.vehicleTypes || [],
      vendorName: manualDriver.vendorName.trim() || 'Independent Driver',
      city: manualDriver.city.trim(),
      area: manualDriver.area.trim(),
      pincode: manualDriver.pincode.trim(),
      rating: null,
      estimatedFare: null,
      location: null,
      distanceKm: null,
      etaMinutes: null,
      isManual: true,
    };
  };

  const handleAssign = (notifyMethod, reason = undefined) => {
    if (!canAssign) return;
    // undefined (not null) so JSON.stringify omits the key entirely when
    // there's no reason (e.g. "Assign via App") — the backend's Joi schema
    // only allows the field to be absent, not explicitly null.
    onAssign({ driver: buildDriver(), prices, notifyMethod, callReason: reason });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col border border-gray-100"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between shrink-0 bg-gray-50/50">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              {mode === 'driver' ? 'Confirm Assignment' : showAssignConfirm ? 'Confirm Assignment' : 'Add & Assign'}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {mode === 'driver'
                ? 'Confirm the driver, then notify them.'
                : showAssignConfirm
                  ? 'Double-check the details before this registers the ambulance and assigns it.'
                  : 'Set the trip price, then notify the driver.'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4 text-sm">
          {mode === 'driver' && driver && (
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <div className="font-semibold text-gray-900 text-base">{driver.name}</div>
              <div className="text-xs text-gray-500 mt-0.5">{driver.vendorName} &middot; {driver.vehicleTypeLabel} &middot; {driver.vehiclePlate}</div>
              <div className="flex items-center gap-4 text-xs text-gray-600 mt-2">
                {driver.phone && (
                  <a
                    href={`tel:${driver.phone}`}
                    className="inline-flex items-center gap-1.5 text-blue-600 font-semibold hover:underline cursor-pointer"
                    title={`Call driver: ${driver.phone}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Phone className="w-3.5 h-3.5" />
                    <span>{driver.phone}</span>
                  </a>
                )}
                {driver.rating && (
                  <span className="inline-flex items-center gap-1 text-amber-500 font-medium">
                    <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                    {driver.rating}
                  </span>
                )}
                {driver.distanceKm != null && (
                  <span className="inline-flex items-center gap-1 text-gray-500">
                    <Navigation className="w-3.5 h-3.5" />
                    {driver.distanceKm.toFixed(1)} km
                  </span>
                )}
              </div>
            </div>
          )}

          {mode === 'manual' && showAssignConfirm && (
            <div className="space-y-4 py-1">
              <div className="bg-blue-50/70 border border-blue-200/70 rounded-xl p-3.5 text-blue-900 text-xs flex items-start gap-2.5">
                <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                <span>
                  Please double-check all details below. Submitting will register this ambulance record and assign it to the booking.
                </span>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 space-y-3">
                <div className="flex items-center justify-between pb-3 border-b border-gray-200/70">
                  <div>
                    <div className="font-semibold text-gray-900 text-sm">{manualDriver.name.trim()}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {manualDriver.vendorName.trim() || 'Independent Driver'}
                    </div>
                  </div>
                  {manualDriver.phone.trim() && (
                    <a
                      href={`tel:${manualDriver.phone.trim()}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 text-xs font-semibold transition-colors"
                      title={`Call driver: ${manualDriver.phone.trim()}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Phone className="w-3.5 h-3.5" />
                      <span>{manualDriver.phone.trim()}</span>
                    </a>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-gray-400 block text-[10px] uppercase font-semibold">Ambulance Type</span>
                    <span className="font-medium text-gray-800">
                      {manualDriver.vehicleTypes?.length > 0
                        ? manualDriver.vehicleTypes
                            .map((typeName) => {
                              const matched = vehicleTypeOptions.find(
                                (t) => t.name === typeName || t.type_id === typeName || t.abbreviation === typeName
                              );
                              return matched?.abbreviation || typeName;
                            })
                            .join(', ')
                        : manualDriver.vehicleType || 'Not specified'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400 block text-[10px] uppercase font-semibold">Vehicle Plate</span>
                    <span className="font-medium text-gray-800">{manualDriver.vehiclePlate.trim() || '—'}</span>
                  </div>
                  {(manualDriver.city || manualDriver.area || manualDriver.pincode) && (
                    <div className="col-span-2">
                      <span className="text-gray-400 block text-[10px] uppercase font-semibold">Location / Area</span>
                      <span className="font-medium text-gray-800">
                        {[manualDriver.area, manualDriver.city, manualDriver.pincode].filter(Boolean).join(', ')}
                      </span>
                    </div>
                  )}
                </div>

                <div className="pt-3 border-t border-gray-200/70">
                  <span className="text-gray-400 block text-[10px] uppercase font-semibold mb-1.5">Pricing Breakdown</span>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                    <div className="bg-white p-2.5 rounded-lg border border-gray-200/80">
                      <span className="text-[10px] text-gray-400 block font-medium">Fleet Op. Price</span>
                      <span className="font-semibold text-gray-800 mt-0.5 block">₹{prices.fleetOperatorPrice || prices.offeredPrice || 0}</span>
                    </div>
                    <div className="bg-white p-2.5 rounded-lg border border-gray-200/80">
                      <span className="text-[10px] text-gray-400 block font-medium">Platform Fee</span>
                      <span className="font-semibold text-gray-800 mt-0.5 block">₹{prices.platformServiceFee || prices.agreedPrice || 0}</span>
                    </div>
                    <div className="bg-white p-2.5 rounded-lg border border-gray-200/80">
                      <span className="text-[10px] text-gray-400 block font-medium">Operator Fee</span>
                      <span className="font-semibold text-gray-800 mt-0.5 block">₹{prices.fleetOperatorServiceFee || prices.partnerPrice || 0}</span>
                    </div>
                    <div className="bg-blue-50/80 p-2.5 rounded-lg border border-blue-200">
                      <span className="text-[10px] text-blue-600 font-bold block">Total Price</span>
                      <span className="font-bold text-blue-900 mt-0.5 block">₹{prices.totalPrice || 0}</span>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-xs text-gray-400 text-center">
                This will also send an ambulance registration request to Admin. Click Edit to adjust any details.
              </p>
            </div>
          )}

          {mode === 'manual' && !showAssignConfirm && (
            <div className="space-y-4">
              {/* Info Notice */}
              <div className="bg-blue-50/70 border border-blue-100 rounded-xl p-3 flex items-start gap-2.5 text-blue-900">
                <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                <p className="text-xs text-blue-800 leading-relaxed">
                  Enter the basic details to assign this ambulance now &mdash; submitting also sends an ambulance
                  registration request to Admin, who will complete the full registration on their side.
                </p>
              </div>

              {/* Driver & Vehicle Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <div>
                  <FieldLabel>Driver Name <span className="text-red-500">*</span></FieldLabel>
                  <Input
                    value={manualDriver.name}
                    onChange={(e) => updateManual('name', e.target.value)}
                    placeholder="Driver name"
                    className="h-9.5 text-sm"
                  />
                </div>
                <div>
                  <FieldLabel>Mobile No <span className="text-red-500">*</span></FieldLabel>
                  <Input
                    value={manualDriver.phone}
                    onChange={(e) => updateManual('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="10-digit mobile number"
                    maxLength={10}
                    className="h-9.5 text-sm"
                  />
                </div>

                {/* Ambulance Type */}
                <div>
                  <FieldLabel>Ambulance Type</FieldLabel>
                  <Select
                    value={selectedVehicleTypeValue}
                    onValueChange={handleVehicleTypeChange}
                    disabled={loadingVehicleTypes}
                  >
                    <SelectTrigger id="add-assign-vehicle-type-select" className="h-9.5 text-sm">
                      <SelectValue placeholder={loadingVehicleTypes ? 'Loading vehicle types…' : 'Select vehicle type'}>
                        {selectedVehicleTypeLabel || undefined}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Select vehicle type</SelectItem>
                      {activeVehicleTypeOptions.map((type) => {
                        const label = type.abbreviation && !type.name.includes(type.abbreviation)
                          ? `${type.name} (${type.abbreviation})`
                          : type.name;
                        return (
                          <SelectItem key={type.type_id || type.id || type.name} value={type.name}>
                            {label}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <FieldLabel>Vehicle No</FieldLabel>
                  <Input
                    value={manualDriver.vehiclePlate}
                    onChange={(e) => updateManual('vehiclePlate', e.target.value.toUpperCase())}
                    placeholder="e.g. GJ01AB1234"
                    className="h-9.5 text-sm uppercase"
                  />
                </div>
                <div className="sm:col-span-2">
                  <FieldLabel>Company Name</FieldLabel>
                  <Input
                    value={manualDriver.vendorName}
                    onChange={(e) => updateManual('vendorName', e.target.value)}
                    placeholder="Fleet / vendor name"
                    className="h-9.5 text-sm"
                  />
                </div>
              </div>

              {/* Location: City, Area, Pincode (matches ambulance search) */}
              <div>
                <FieldLabel className="flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-blue-500" />
                  <span>Ambulance Location / Operating Area</span>
                </FieldLabel>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  <div>
                    <Input
                      type="text"
                      placeholder="City (e.g. Surat)"
                      value={manualDriver.city}
                      onChange={(e) => updateManual('city', e.target.value)}
                      className="h-9.5 text-xs sm:text-sm"
                    />
                  </div>
                  <div>
                    <Input
                      type="text"
                      placeholder="Area (e.g. Varachha)"
                      value={manualDriver.area}
                      onChange={(e) => updateManual('area', e.target.value)}
                      className="h-9.5 text-xs sm:text-sm"
                    />
                  </div>
                  <div>
                    <Input
                      type="text"
                      placeholder="Pincode (395006)"
                      value={manualDriver.pincode}
                      onChange={(e) => updateManual('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                      className="h-9.5 text-xs sm:text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Pricing Breakdown: 4 boxes in 1 single row */}
              <div className="bg-slate-50/70 border border-slate-200/80 rounded-xl p-3.5 space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-700">Trip Pricing</span>
                  <span className="text-[11px] text-gray-400">Total auto-calculates</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                  <div>
                    <label className="text-[11px] font-medium text-gray-500 mb-1 block truncate">Fleet Operator Price</label>
                    <div className="relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">₹</span>
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        className="pl-6 h-9 text-xs sm:text-sm bg-white"
                        value={prices.fleetOperatorPrice ?? prices.offeredPrice ?? ''}
                        onChange={(e) => handlePriceFieldChange('fleetOperatorPrice', e.target.value)}
                        placeholder="0"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-gray-500 mb-1 block truncate">Platform Service Fee</label>
                    <div className="relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">₹</span>
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        className="pl-6 h-9 text-xs sm:text-sm bg-white"
                        value={prices.platformServiceFee ?? prices.agreedPrice ?? ''}
                        onChange={(e) => handlePriceFieldChange('platformServiceFee', e.target.value)}
                        placeholder="0"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-gray-500 mb-1 block truncate">Operator Service Fee</label>
                    <div className="relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">₹</span>
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        className="pl-6 h-9 text-xs sm:text-sm bg-white"
                        value={prices.fleetOperatorServiceFee ?? prices.partnerPrice ?? ''}
                        onChange={(e) => handlePriceFieldChange('fleetOperatorServiceFee', e.target.value)}
                        placeholder="0"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-gray-800 mb-1 block truncate">Total Price</label>
                    <div className="flex h-9 w-full items-center px-2.5 rounded-md border border-blue-200 bg-blue-50/80 text-xs sm:text-sm font-bold text-blue-900 select-none">
                      {prices.totalPrice !== '' && prices.totalPrice != null
                        ? `₹${Number(prices.totalPrice).toLocaleString('en-IN')}`
                        : (() => {
                            const fo = Number(prices.fleetOperatorPrice ?? prices.offeredPrice ?? 0);
                            const ps = Number(prices.platformServiceFee ?? prices.agreedPrice ?? 0);
                            const fos = Number(prices.fleetOperatorServiceFee ?? prices.partnerPrice ?? 0);
                            const sum = fo + ps + fos;
                            return sum > 0 ? `₹${sum.toLocaleString('en-IN')}` : '₹0';
                          })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3.5 border-t border-gray-100 bg-gray-50/40 shrink-0">
          {hasApp ? (
            <div className="space-y-2">
              <button
                disabled={!canAssign}
                onClick={() => handleAssign('app')}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed text-primary-foreground text-sm font-semibold transition-colors shadow-sm"
              >
                {assigning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Smartphone className="w-4 h-4" />}
                Assign via App
              </button>

              {!showCallReason ? (
                <button
                  onClick={() => setShowCallReason(true)}
                  className="w-full inline-flex items-center justify-center px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 border border-gray-200 text-gray-700 text-xs font-medium transition-colors"
                >
                  Assign by Call instead
                </button>
              ) : (
                <div className="pt-1 space-y-2">
                  <FieldLabel>Reason for calling instead <span className="text-red-500">*</span></FieldLabel>
                  <Select value={callReason} onValueChange={setCallReason}>
                    <SelectTrigger className="bg-white border-gray-300">
                      <SelectValue placeholder="Select a reason..." />
                    </SelectTrigger>
                    <SelectContent side="top">
                      {CALL_REASONS.map((reason) => (
                        <SelectItem key={reason} value={reason}>{reason}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setShowCallReason(false); setCallReason(''); }}
                      className="flex-1 inline-flex items-center justify-center px-3 py-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 text-xs font-medium transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      disabled={!canAssign || !callReason}
                      onClick={() => handleAssign('call', callReason)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border border-primary/30 text-primary hover:bg-primary/5 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-semibold transition-colors"
                    >
                      {assigning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Phone className="w-3.5 h-3.5" />}
                      Confirm Call
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : showAssignConfirm ? (
            <div className="flex gap-3">
              <button
                onClick={() => setShowAssignConfirm(false)}
                disabled={assigning}
                className="flex-1 inline-flex items-center justify-center px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-semibold transition-colors"
              >
                Edit
              </button>
              <button
                disabled={!canAssign}
                onClick={() => handleAssign('call')}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed text-primary-foreground text-sm font-semibold shadow-sm transition-all"
              >
                {assigning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
                Confirm &amp; Assign
              </button>
            </div>
          ) : (
            <button
              disabled={!canAssign}
              onClick={() => setShowAssignConfirm(true)}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed text-primary-foreground text-sm font-semibold shadow-sm transition-all"
            >
              <Phone className="w-4 h-4" />
              Assign by Call
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommandCenterAssignPricingPopup;
